import React, { useEffect, useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import routes from 'routes';
import { createMeet, updateMeet, getMeetDefaults } from 'api/clubApi';
import useMeetForm from 'hooks/useMeetForm';
import {
  getMeetFormData,
  getMeetRequest,
  meetKeys,
  meetPointsRulesRequest,
} from 'services/admin/MeetService';
import { updateMeetVolunteerRequirements } from 'api/volunteerApi';

import MeetForm from 'components/admin/MeetForm';
import Loading from 'components/common/Loading';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import {
  updateMeetProgram,
  getMeetComplete,
  deleteMeetProgram,
} from 'api/meetApi';
import _ from 'lodash';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { useBlocker } from 'hooks/useChangeDetection';
import CopyFromMeetPopup from 'components/admin/CopyFromMeetPopup';
import ImpactReviewDialog from 'components/admin/ImpactReviewDialog';
import { getVolunteerRoles } from 'api/volunteerApi';
import { getClubEventTypes } from 'api/club/eventTypeApi';
import { requirementResolvesToProgram } from 'utils/volunteerProgramUtils';
import { clubFeaturesOf, clubForId } from 'utils/clubFeatures';
import { copyPointsRules, pointsRulesForOrganisation } from 'utils/pointsRules';

// Requirements map -> requirements PUT payload (included rows only)
const transformRequirementsForAPI = (requirements) => {
  return Object.entries(requirements)
    .filter(([, req]) => req && req.include)
    .map(([, req]) => ({
      meetVolunteerRequirementID: req.meetVolunteerRequirementID || undefined,
      volunteerRoleID: req.volunteerRoleID,
      numberRequired: req.numberRequired || 1,
      ageGroupCode: req.ageGroupCode || undefined,
      programGroupName: req.programGroupName || undefined,
      groupType: req.groupType || undefined,
    }));
};

// True when the error is the program save/delete's Impact Review 409
// (docs/program-edit-impact-design.html §7.1)
const isImpactConflict = (err) =>
  err.response?.status === 409 &&
  err.response.data?.errorCode === 'ProgramImpactResolutionRequired';

// True when the error is the requirements PUT's review 409 — removing
// requirements would cancel open signups and the resolutions didn't cover
// them (volunteer-role-deletion-design.md §16).
const isRequirementsImpactConflict = (err) =>
  err.response?.status === 409 &&
  err.response.data?.errorCode === 'RequirementRemovalCancelsSignups';

// Zero-signup requirement removals apply WITHOUT the Impact Review dialog
// (blueprint §7.1 threshold: only signups/start lists/registrations gate the
// save). Name what the program save auto-removed and drop those rows from
// the requirements map so the follow-up requirements PUT can't reference
// them. Returns the map unchanged when nothing was auto-removed.
const applyAutoRemovedRequirements = (applied, requirementsMap) => {
  if (!applied || !(applied.requirementsRemoved > 0)) return requirementsMap;
  if (applied.removedRequirementNames?.length > 0) {
    toast.info(
      `Also removed volunteer roles for events no longer in the program: ${applied.removedRequirementNames.join(', ')}`,
      { autoClose: 8000 }
    );
  }
  const removedIds = new Set(applied.removedRequirementIds || []);
  if (removedIds.size === 0) return requirementsMap;
  return Object.fromEntries(
    Object.entries(requirementsMap).filter(
      ([, req]) => !removedIds.has(req?.meetVolunteerRequirementID)
    )
  );
};

// Whether the meet's own points differ between two sets of form values (the meet keys compare
// useCustomPoints only; the rules are an object).
const meetPointsChanged = (formValues, originalValues) =>
  JSON.stringify(meetPointsRulesRequest(formValues)) !==
  JSON.stringify(meetPointsRulesRequest(originalValues));

const MeetManager = ({ mode, clubId, meetId }) => {
  const navigate = useNavigate();
  // A school is not offered season-best points: the meet's own points are checked without them.
  const club = useSelector((state) => clubForId(state.user, clubId));
  const { seasonBestPoints, teams: hasHouses } = clubFeaturesOf(club);
  const [fetchingMeet, setFetchingMeet] = useState(true);
  const [originalMeetProgram, setOriginalMeetProgram] = useState(); // using to check update status
  const [originalMeetFormValues, setOriginalMeetFormValues] = useState(); // using to check update status
  const [showPrompt, setShowPrompt] = useState(false);
  const [retry, setRetry] = useState(null);
  const [showCopyFromMeetPopup, setShowCopyFromMeetPopup] = useState(false);
  const [sourceMeet, setSourceMeet] = useState(null);
  const [copyingSettings, setCopyingSettings] = useState(false);
  // Impact Review: context stashed when the program save/delete returns the
  // 409 impact report; the dialog resumes the save via handleImpactConfirm.
  const [impactContext, setImpactContext] = useState(null);
  const [impactSaving, setImpactSaving] = useState(false);
  // Same shape for the requirements PUT's 409 (removals cancelling open
  // signups); resumed via handleRequirementsImpactConfirm.
  const [requirementsImpactContext, setRequirementsImpactContext] =
    useState(null);
  const [requirementsImpactSaving, setRequirementsImpactSaving] =
    useState(false);

  // Helper function to normalize high jump settings for comparison
  const normalizeHighJumpSettings = (settings) => {
    if (!settings) return null;

    return {
      eliminationMode: settings.eliminationMode || 'consecutiveMisses',
      maxMisses: settings.maxMisses || 3,
      minimumStartHeights: (settings.minimumStartHeights || []).map(height => ({
        ageRange: height.ageRange || '',
        femaleHeight: height.femaleHeight !== undefined && height.femaleHeight !== null && height.femaleHeight !== 0
          ? parseFloat(height.femaleHeight)
          : null,
        maleHeight: height.maleHeight !== undefined && height.maleHeight !== null && height.maleHeight !== 0
          ? parseFloat(height.maleHeight)
          : null,
      })),
    };
  };

  useBlocker((retryFn) => {
    if (
      meetKeys.some((key) => formValues[key] !== originalMeetFormValues[key]) ||
      meetPointsChanged(formValues, originalMeetFormValues) ||
      JSON.stringify(formValues.programData) !==
        JSON.stringify(originalMeetProgram) ||
      JSON.stringify(normalizeHighJumpSettings(formValues.highJumpSettings)) !==
        JSON.stringify(normalizeHighJumpSettings(originalMeetFormValues.highJumpSettings)) ||
      formValues.requirementsModified === true
    ) {
      setShowPrompt(true);
      setRetry(() => retryFn);
      return false;
    }
    return true;
  }, true);

  const confirmLeave = () => {
    if (retry) {
      retry(); // trigger navigation
      setRetry(null);
    }
    setShowPrompt(false);
  };

  const cancelLeave = () => {
    setRetry(null); // discard navigation
    setShowPrompt(false);
  };

  const initialFormValues = {
    meetName: '',
    date: '',
    time: '',
    description: '',
    meetType: mode === 'edit' ? 1 : '',
    location: '',
    ageGroups: [],
    multiLaneEvents: [],
    hurdleEvents: [],
    relayEvents: [],
    groupDistanceEvents: [],
    walkEvents: [],
    sprintAgilityTraining: [],
    fieldEvents: [],
    meetStatus: '',
    countsForSeasonPoints: true,
    countsForSeasonBests: true,
    useCustomPoints: false,
    // The club's points rules, and the meet's own (utils/pointsRules.js), from the API.
    defaultPointsRules: null,
    customPointsRules: null,
    highJumpSettings: {
      eliminationMode: 'consecutiveMisses',
      maxMisses: 3,
      minimumStartHeights: [],
    },
    ageEventMap: '',
    timezone: '', // read-only; inherited from the club (set in club settings)
    // Meet signup config (§6.8) — multi-club only; ignored by the server otherwise
    selfServiceRegistrationEnabled: false,
    publicSignupEnabled: false,
    signupCode: null,
    signupOpenDate: '',
    signupOpenTime: '',
    signupCloseDate: '',
    signupCloseTime: '',
    registrationCap: 0,
  };

  const {
    formValues,
    setFormValues,
    errors,
    setErrors,
    validateForm,
    handleInputChange,
    handleRadioChange,
    handleTimeChange,
    loading,
    setLoading,
  } = useMeetForm(initialFormValues);

  const fetchMeetComplete = useCallback(async () => {
    if (mode !== 'edit') return;

    try {
      const response = await getMeetComplete(clubId, meetId);
      const { meet, program } = response.data;

      // Transform meet data to form format. A school's hidden season-best points read as 0
      // (utils/pointsRules.js, pointsRulesForOrganisation), in the club's points and the meet's own.
      const meetFormData = getMeetFormData(meet);
      const formData = {
        ...meetFormData,
        defaultPointsRules: pointsRulesForOrganisation(meetFormData.defaultPointsRules, seasonBestPoints),
        customPointsRules: pointsRulesForOrganisation(meetFormData.customPointsRules, seasonBestPoints),
      };

      // Transform program data
      let programData = { rotationCount: 4, programGroups: [] };
      if (program) {
        programData = {
          rotationCount: program.rotationCount,
          programGroups: program.programGroups.map((group) => ({
            ...group,
            displayName:
              group.displayName ||
              (group.gender && group.gender !== 'Mixed'
                ? `${group.groupName} ${group.gender}`
                : group.groupName),
          })),
        };
      }

      const highJumpSettings = meet.highJumpSettings || {
        eliminationMode: 'consecutiveMisses',
        maxMisses: 3,
        minimumStartHeights: [],
      };

      // Set all form values at once
      setFormValues({
        ...formData,
        programData: programData,
        ageEventMap: meet.ageEventMapping || '',
        highJumpSettings: highJumpSettings,
        volunteerRequirements: response.data.volunteerRequirements,
      });

      // Store originals for change detection
      setOriginalMeetFormValues(_.cloneDeep({
        ...formData,
        highJumpSettings: highJumpSettings,
      }));
      setOriginalMeetProgram(_.cloneDeep(programData));
    } catch (err) {
      if (err.status === 404) {
        navigate(routes.notFound);
      } else {
        toast.error(err.message);
        navigate(routes.admin.dashboard);
      }
    }
  }, [mode, clubId, meetId, setFormValues, navigate, seasonBestPoints]);

  const fetchMeetDefaults = useCallback(async () => {
    if (mode !== 'add') return;

    try {
      const response = await getMeetDefaults(clubId);
      const defaults = response.data;
      const programData = { rotationCount: 4, programGroups: [] };
      const formData = {
        ...initialFormValues,
        programData: programData,
        meetType:
          defaults.defaultNewMeetType?.toString() || initialFormValues.meetType,
        time: defaults.defaultStartTime || initialFormValues.time,
        location: defaults.defaultLocation || initialFormValues.location,
        timezone: defaults.defaultTimezone || initialFormValues.timezone,
        countsForSeasonPoints: true,
        countsForSeasonBests: true,
        useCustomPoints: false,
        highJumpSettings: defaults.highJumpSettings || {
          eliminationMode: 'consecutiveMisses',
          maxMisses: 3,
          minimumStartHeights: [],
        },
        // The club's points rules; a new meet uses them until the admin chooses different points. A
        // school's hidden season-best points read as 0 (utils/pointsRules.js).
        defaultPointsRules: pointsRulesForOrganisation(defaults.defaultPointsRules || null, seasonBestPoints),
        customPointsRules: null,
      };
      // Update form values with defaults
      setFormValues(formData);
      setOriginalMeetFormValues(_.cloneDeep(formData));
      setOriginalMeetProgram(_.cloneDeep(programData));
    } catch (err) {
      console.log('[MeetManager] Fetch meet defaults error: ', err);
      // Don't show error to user as defaults are not critical
    }
    // eslint-disable-next-line
  }, [mode, clubId, setFormValues, seasonBestPoints]);

  useEffect(() => {
    setFetchingMeet(true);
    if (mode === 'edit') {
      fetchMeetComplete().then(() => setFetchingMeet(false));
    } else {
      fetchMeetDefaults().then(() => setFetchingMeet(false));
    }
  }, [mode, fetchMeetComplete, fetchMeetDefaults]);

  const handleButton = async (requirements) => {
    const validationErrors = validateForm({ seasonBestPoints, relaysOffered: hasHouses });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    const meetPayload = getMeetRequest(formValues);

    try {
      let currentMeetId = meetId;
      let requirementsForPut = requirements;

      // Check what has changed
      const meetChanged = meetKeys.some(
        (key) => formValues[key] !== originalMeetFormValues[key]
      ) || meetPointsChanged(formValues, originalMeetFormValues) || JSON.stringify(normalizeHighJumpSettings(formValues.highJumpSettings)) !==
        JSON.stringify(normalizeHighJumpSettings(originalMeetFormValues.highJumpSettings));
      const programChanged =
        JSON.stringify(formValues.programData) !==
        JSON.stringify(originalMeetProgram);
      // requirementsChanged is evaluated in finishSaveAfterProgram

      // Step 1: Save or create meet (always save if mode === 'add')
      if (mode === 'add') {
        const response = await createMeet({
          clubID: clubId,
          publishingAccess: 'AdminOnly',
          ...meetPayload,
        });
        currentMeetId = response.data.meetID;
        toast.success('Meet created', { autoClose: 3000 });
      } else if (meetChanged) {
        await updateMeet(meetId, meetPayload);
        toast.success('Meet details saved', { autoClose: 3000 });
      }

      // Step 2: Save program if changed (or if new meet)
      if (mode === 'add' || programChanged) {
        const programDataPayload = {
          meetId: currentMeetId,
          rotationCount: formValues.programData.rotationCount,
          programGroups: formValues.programData.programGroups.map((group) => ({
            groupName: group.groupName,
            groupSource: group.groupSource,
            sourceGroupID: group.sourceGroupID,
            ageRange: group.ageRange,
            gender: group.gender,
            displayOrder: group.displayOrder,
            maxEventsForGroup: group.maxEventsForGroup ?? null, // §6.4 per-group event cap (null = unlimited)
            combineAges: Boolean(group.combineAges), // step 8: the row's events give one set of places for the group
            events: group.events.map((event) => ({
              ...event,
              eventType:
                !event.eventType ||
                event.eventType === '' ||
                event.eventType === null
                  ? 1200 // Default to '-' for empty events
                  : event.eventType,
            })),
          })),
        };
        if (programDataPayload.programGroups.length > 0) {
          try {
            // Call the update meet program API only if there’s at least one program.
            const response = await updateMeetProgram(
              currentMeetId,
              programDataPayload
            );
            toast.success('Program configuration saved', { autoClose: 3000 });
            requirementsForPut = applyAutoRemovedRequirements(
              response.data?.appliedResolutions,
              requirementsForPut
            );
          } catch (err) {
            if (isImpactConflict(err)) {
              // The save orphans volunteers/start lists/registrations — show
              // the Impact Review dialog; handleImpactConfirm resumes the save
              // with resolutions attached. Nothing has been saved server-side.
              setImpactContext({
                report: err.response.data,
                isDelete: false,
                programDataPayload,
                requirements,
                currentMeetId,
                meetChanged,
              });
              setLoading(false);
              return;
            }
            throw err;
          }
        } else if (mode === 'edit') {
          try {
            const response = await deleteMeetProgram(currentMeetId);
            toast.success('Program configuration deleted', { autoClose: 3000 });
            requirementsForPut = applyAutoRemovedRequirements(
              response.data?.appliedResolutions,
              requirementsForPut
            );
          } catch (err) {
            if (isImpactConflict(err)) {
              setImpactContext({
                report: err.response.data,
                isDelete: true,
                programDataPayload: null,
                requirements,
                currentMeetId,
                meetChanged,
              });
              setLoading(false);
              return;
            }
            throw err;
          }
        }
      }

      await finishSaveAfterProgram(currentMeetId, requirementsForPut, {
        meetChanged,
        programChanged,
      });
    } catch (err) {
      console.error(err);
      const errorData = err.response?.data;
      const message =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || 'An error occurred';
      // A 409 that names a field (e.g. a same-name meet already exists on this date) is a
      // form error, so pin it to that input as well as toasting it.
      if (err.response?.status === 409 && errorData?.field) {
        setErrors((prev) => ({ ...prev, [errorData.field]: message }));
      }
      toast.error(message);
      setLoading(false);
    }
  };

  // Post-requirements finalize + navigate. Shared by the direct save path and
  // both impact-confirm paths.
  const finalizeSave = (
    currentMeetId,
    { meetChanged, programChanged, requirementsChanged }
  ) => {
    // Show a message if nothing changed
    if (
      mode === 'edit' &&
      !meetChanged &&
      !programChanged &&
      !requirementsChanged
    ) {
      toast.info('No changes to save', { autoClose: 2000 });
    }

    // Update original data
    setOriginalMeetFormValues(_.cloneDeep(formValues));
    setOriginalMeetProgram(_.cloneDeep(formValues.programData));

    handleInputChange({
      target: {
        name: 'requirementsModified',
        value: false,
      },
    });

    // Navigate after a delay
    setTimeout(() => {
      navigate(routes.admin.showMeet.url(clubId, currentMeetId));
    }, 2000);
  };

  // Steps after the program save: requirements PUT + finalize + navigate.
  // Shared by the direct save path and the Impact Review confirm path.
  const finishSaveAfterProgram = async (
    currentMeetId,
    requirementsMap,
    { meetChanged, programChanged }
  ) => {
    const requirementsChanged = formValues.requirementsModified === true;

    // Step 3: Save requirements if changed (or if new meet)
    if (mode === 'add' || requirementsChanged) {
      const apiRequirements = transformRequirementsForAPI(requirementsMap);
      try {
        await updateMeetVolunteerRequirements(currentMeetId, apiRequirements);
      } catch (err) {
        if (isRequirementsImpactConflict(err)) {
          // Removing requirements cancels open signups — show the review
          // dialog; handleRequirementsImpactConfirm resumes the save with the
          // cancellations acknowledged. Nothing requirements-related has been
          // saved server-side.
          setRequirementsImpactContext({
            report: err.response.data,
            currentMeetId,
            requirementsMap,
            meetChanged,
            programChanged,
          });
          setLoading(false);
          return;
        }
        throw err;
      }
      toast.success('Volunteer requirements saved', { autoClose: 3000 });
    }

    finalizeSave(currentMeetId, {
      meetChanged,
      programChanged,
      requirementsChanged,
    });
  };

  // Requirements review confirm: re-submit the same requirements PUT with the
  // acknowledged cancellations attached, then finish the interrupted save.
  const handleRequirementsImpactConfirm = async (resolutions) => {
    if (!requirementsImpactContext) return;
    const { currentMeetId, requirementsMap, meetChanged, programChanged } =
      requirementsImpactContext;

    setRequirementsImpactSaving(true);
    setLoading(true);
    try {
      const apiRequirements = transformRequirementsForAPI(requirementsMap);
      const cancelSignupIds = resolutions?.cancelSignupIds || [];
      await updateMeetVolunteerRequirements(
        currentMeetId,
        apiRequirements,
        cancelSignupIds
      );
      toast.success('Volunteer requirements saved', { autoClose: 3000 });
      if (cancelSignupIds.length > 0) {
        toast.info(
          `${cancelSignupIds.length} volunteer signup${
            cancelSignupIds.length === 1 ? ' was' : 's were'
          } cancelled.`,
          { autoClose: 8000 }
        );
      }

      setRequirementsImpactContext(null);
      finalizeSave(currentMeetId, {
        meetChanged,
        programChanged,
        requirementsChanged: true,
      });
    } catch (err) {
      if (isRequirementsImpactConflict(err)) {
        // Race safety: a signup arrived between the report and the confirm —
        // show the refreshed report instead of applying.
        setRequirementsImpactContext((prev) =>
          prev ? { ...prev, report: err.response.data } : prev
        );
        toast.info(
          'The meet changed while you were reviewing — please review the updated impact.'
        );
        setLoading(false);
      } else {
        console.error(err);
        const errorData = err.response?.data;
        toast.error(
          typeof errorData === 'string'
            ? errorData
            : errorData?.message || 'An error occurred'
        );
        setRequirementsImpactContext(null);
        setLoading(false);
      }
    } finally {
      setRequirementsImpactSaving(false);
    }
  };

  const handleRequirementsImpactCancel = () => {
    // Nothing was saved server-side for requirements — the admin is back in
    // the editor with their unsaved changes intact.
    setRequirementsImpactContext(null);
  };

  // Impact Review confirm: re-submit the same program save/delete with the
  // resolutions attached, then continue the interrupted save flow.
  const handleImpactConfirm = async (resolutions) => {
    if (!impactContext) return;
    const {
      report,
      isDelete,
      programDataPayload,
      requirements,
      currentMeetId,
      meetChanged,
    } = impactContext;

    setImpactSaving(true);
    setLoading(true);
    try {
      let response;
      if (isDelete) {
        response = await deleteMeetProgram(currentMeetId, resolutions);
        toast.success('Program configuration deleted', { autoClose: 3000 });
      } else {
        response = await updateMeetProgram(currentMeetId, {
          ...programDataPayload,
          resolutions,
        });
        toast.success('Program configuration saved', { autoClose: 3000 });
      }

      const applied = response.data?.appliedResolutions;
      if (applied?.signupsCancelled > 0) {
        toast.info(
          `${applied.signupsCancelled} volunteer signup${
            applied.signupsCancelled === 1 ? ' was' : 's were'
          } cancelled — use Manage Signups on the meet page to sign volunteers up for the new roles.`,
          { autoClose: 8000 }
        );
      }

      // Drop the requirements the save just removed so the requirements PUT
      // cannot resurrect them (blueprint §7 integration rule).
      const removedIds = new Set(
        [
          ...(report.requirementsWithoutSignups || []),
          ...(report.requirementsWithSignups || []),
        ].map((requirement) => requirement.meetVolunteerRequirementID)
      );
      const filteredRequirements = Object.fromEntries(
        Object.entries(requirements).filter(
          ([, req]) => !removedIds.has(req?.meetVolunteerRequirementID)
        )
      );

      setImpactContext(null);
      await finishSaveAfterProgram(currentMeetId, filteredRequirements, {
        meetChanged,
        programChanged: true,
      });
    } catch (err) {
      if (isImpactConflict(err)) {
        // Race safety: something changed between the report and the confirm
        // (e.g. a new signup) — show the refreshed report instead of applying.
        setImpactContext((prev) =>
          prev ? { ...prev, report: err.response.data } : prev
        );
        toast.info(
          'The meet changed while you were reviewing — please review the updated impact.'
        );
        setLoading(false);
      } else {
        console.error(err);
        const errorData = err.response?.data;
        toast.error(
          typeof errorData === 'string'
            ? errorData
            : errorData?.message || 'An error occurred'
        );
        setImpactContext(null);
        setLoading(false);
      }
    } finally {
      setImpactSaving(false);
    }
  };

  const handleImpactCancel = () => {
    // Nothing was saved server-side — the admin is back in the editor with
    // their unsaved program edits intact.
    setImpactContext(null);
  };

  const handleCancel = () => {
    if (meetId) {
      navigate(routes.admin.showMeet.url(clubId, meetId));
      return;
    }
    navigate(routes.admin.meets);
  };

  // Handle copying settings from a selected source meet
  const handleCopyFromMeet = useCallback(async (selectedMeet) => {
    if (!selectedMeet?.meetID) return;

    setCopyingSettings(true);
    try {
      // Fetch complete details of the source meet
      const response = await getMeetComplete(clubId, selectedMeet.meetID);
      const { meet, program, volunteerRequirements } = response.data;

      // Transform program data (remove IDs so new ones will be created)
      let programData = { rotationCount: 4, programGroups: [] };
      if (program) {
        programData = {
          rotationCount: program.rotationCount,
          programGroups: program.programGroups.map((group) => ({
            ...group,
            displayName:
              group.displayName ||
              (group.gender && group.gender !== 'Mixed'
                ? `${group.groupName} ${group.gender}`
                : group.groupName),
          })),
        };
      }

      const highJumpSettings = meet.highJumpSettings || {
        eliminationMode: 'consecutiveMisses',
        maxMisses: 3,
        minimumStartHeights: [],
      };

      // Copy-time filter (program-edit-impact-design.html §8.1): drop source
      // requirements that don't resolve against the copied program — legacy
      // orphans must not propagate into the new meet. The requirements PUT
      // enforces the same rule server-side; filtering here keeps honest copies
      // clean instead of failing the save.
      let sourceRequirements = volunteerRequirements || [];
      if (sourceRequirements.length > 0) {
        try {
          const [rolesResponse, eventTypesResponse] = await Promise.all([
            getVolunteerRoles(clubId),
            getClubEventTypes(clubId, false),
          ]);
          const roleById = new Map(
            (rolesResponse.data || []).map((role) => [role.volunteerRoleID, role])
          );
          // Two distinct drop reasons, reported separately and by name —
          // a bare count reads as noise and erodes trust in the copy:
          // - the role itself was deleted from the club (kept on the source
          //   meet as history); without this check it would ride along
          //   invisibly and fail the save with "Invalid role IDs"
          // - the role exists but no longer resolves against the source
          //   meet's program (legacy orphan requirement)
          const deletedRoleNames = [];
          const notInProgramNames = [];
          const resolvable = sourceRequirements.filter((req) => {
            const role = roleById.get(req.volunteerRoleID);
            if (!role) {
              deletedRoleNames.push(req.roleName || 'Unnamed role');
              return false;
            }
            if (
              !requirementResolvesToProgram(
                req,
                role,
                programData.programGroups,
                eventTypesResponse.data || []
              )
            ) {
              notInProgramNames.push(
                req.programGroupName
                  ? `${role.roleName} (${req.programGroupName})`
                  : role.roleName
              );
              return false;
            }
            return true;
          });
          if (deletedRoleNames.length > 0) {
            toast.info(
              `Not copied — ${deletedRoleNames.length === 1 ? 'this volunteer role no longer exists' : 'these volunteer roles no longer exist'} in your club: ${[...new Set(deletedRoleNames)].join(', ')}`,
              { autoClose: 10000 }
            );
          }
          if (notInProgramNames.length > 0) {
            toast.info(
              `Not copied — ${notInProgramNames.length === 1 ? 'this volunteer role is' : 'these volunteer roles are'} not covered by the meet's event program: ${[...new Set(notInProgramNames)].join(', ')}`,
              { autoClose: 10000 }
            );
          }
          sourceRequirements = resolvable;
        } catch (rolesErr) {
          // The server-side create guard still protects the save.
          console.error('Could not load volunteer roles for copy filtering:', rolesErr);
        }
      }

      // Transform volunteer requirements (remove IDs so new ones will be created)
      const transformedVolunteerRequirements = sourceRequirements.map((req) => ({
        ...req,
        meetVolunteerRequirementID: undefined, // Remove ID so new ones are created
        numberCommitted: 0, // Signups don't carry over to a new meet
      }));

      // Extract time from source meet's meetDate (format: "2024-01-15T09:00:00")
      let sourceTime = prev => prev.time;
      if (meet.meetDate && meet.meetDate.includes('T')) {
        const timePart = meet.meetDate.split('T')[1];
        if (timePart) {
          const [hours, minutes] = timePart.split(':');
          sourceTime = `${hours}:${minutes}`;
        }
      }

      // Update form values with copied data but preserve some fields
      // We don't copy: meetName, date, meetStatus (keep as new meet defaults)
      // We DO copy: time (start time from source meet)
      setFormValues((prev) => ({
        ...prev,
        // Copy these settings from source meet:
        time: typeof sourceTime === 'string' ? sourceTime : sourceTime(prev),
        meetType: meet.meetType?.toString() || prev.meetType,
        location: meet.location || prev.location,
        description: meet.description || prev.description,
        countsForSeasonPoints: meet.countsForSeasonPoints ?? true,
        countsForSeasonBests: meet.countsForSeasonBests ?? meet.countsForSeasonPoints ?? true,
        // The source meet's own points, when it has them; otherwise the new meet uses the club's.
        useCustomPoints: Boolean(meet.customPointsRules),
        customPointsRules: meet.customPointsRules
          ? copyPointsRules(pointsRulesForOrganisation(meet.customPointsRules, seasonBestPoints))
          : null,
        highJumpSettings: highJumpSettings,
        programData: programData,
        volunteerRequirements: transformedVolunteerRequirements,
        // Do NOT copy signup settings — a copied meet defaults to signup off (§6.8). The admin enables it per meet,
        // and turning it on seeds fresh registration dates from this meet's own date.
        selfServiceRegistrationEnabled: false,
        publicSignupEnabled: false,
        signupCode: null,
        signupOpenDate: '',
        signupOpenTime: '',
        signupCloseDate: '',
        signupCloseTime: '',
        registrationCap: 0,
        // Mark as requirements modified since we're copying them
        requirementsModified: true,
      }));

      // Store source meet info for display
      setSourceMeet({
        meetID: selectedMeet.meetID,
        meetName: selectedMeet.meetName,
        meetDate: selectedMeet.meetDate,
        seasonName: selectedMeet.seasonName || null,
      });

      toast.success(
        `Settings copied from "${selectedMeet.meetName}"${
          selectedMeet.seasonName ? ` (${selectedMeet.seasonName})` : ''
        }`
      );
    } catch (err) {
      console.error('Error copying meet settings:', err);
      toast.error('Failed to copy meet settings. Please try again.');
    } finally {
      setCopyingSettings(false);
    }
  }, [clubId, setFormValues, seasonBestPoints]);

  if (fetchingMeet) {
    return (
      <AdminTablePageLayout loading={true}>
        <Loading />
      </AdminTablePageLayout>
    );
  }

  return (
    <>
      <AdminTablePageLayout loading={loading}>
        <MeetPageHeader
          title={mode === 'add' ? 'Add Meet' : 'Edit Meet'}
          meetName={mode === 'edit' ? formValues.meetName : undefined}
          subTitle={
            // One wording for every organisation (Doug, 2026-10-29): a school
            // read "for your club".
            mode === 'add'
              ? 'Create a new competition or training session'
              : undefined
          }
        >
          {mode === 'add' && (
            <button
              onClick={() => setShowCopyFromMeetPopup(true)}
              disabled={copyingSettings}
              style={{
                padding: '8px 16px',
                border: '1px solid #cf2c47',
                background: 'white',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: copyingSettings ? 'not-allowed' : 'pointer',
                color: '#cf2c47',
                opacity: copyingSettings ? 0.6 : 1,
              }}
            >
              {copyingSettings ? 'Copying...' : 'Copy from Existing Meet'}
            </button>
          )}
          <button
            onClick={handleCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              background: 'white',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#374151',
            }}
          >
            Cancel
          </button>
        </MeetPageHeader>

        <MeetForm
          clubId={clubId}
          mode={mode}
          meetId={meetId}
          formValues={formValues}
          handleButton={handleButton}
          handleInputChange={handleInputChange}
          handleRadioChange={handleRadioChange}
          handleTimeChange={handleTimeChange}
          errors={errors}
          loading={loading}
          sourceMeet={sourceMeet}
        />
      </AdminTablePageLayout>
      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Leave this page?"
        onConfirm={confirmLeave}
        confirmButtonText="OK"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
      ></CustomNewConfirm>
      {mode === 'add' && (
        <CopyFromMeetPopup
          clubId={clubId}
          showPopup={showCopyFromMeetPopup}
          closePopup={() => setShowCopyFromMeetPopup(false)}
          onSelectMeet={handleCopyFromMeet}
        />
      )}
      <ImpactReviewDialog
        isOpen={impactContext !== null}
        report={impactContext?.report || null}
        onCancel={handleImpactCancel}
        onConfirm={handleImpactConfirm}
        saving={impactSaving}
      />
      {/* Requirements-save review: removals that cancel open signups (§16) */}
      <ImpactReviewDialog
        isOpen={requirementsImpactContext !== null}
        report={requirementsImpactContext?.report || null}
        onCancel={handleRequirementsImpactCancel}
        onConfirm={handleRequirementsImpactConfirm}
        saving={requirementsImpactSaving}
        title="Review Volunteer Changes"
        description="Removing these volunteer roles cancels the signups listed below. Everything listed will be applied together when you select Save Requirements. Nothing changes if you cancel."
        confirmButtonText="Save Requirements"
      />
    </>
  );
};

MeetManager.propTypes = {
  mode: PropTypes.string,
  clubId: PropTypes.string,
  meetId: PropTypes.string,
};

export default MeetManager;
